import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiSearch, FiUploadCloud } from "react-icons/fi";
import "./Quality.css";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { CaseResult, MockBanner, SeverityChip, hasPerm } from "./qualityUi";
import TestCaseForm from "./TestCaseForm";
import TestCaseImportModal from "./TestCaseImportModal";

/**
 * The "Test Cases" tab in the Sprint ticket workspace.
 *
 * QA links the test cases that prove a ticket works. They live in the global
 * library, so every later regression run for the ticket's module picks them
 * up, and a release snapshot records which cases covered the ticket when it
 * shipped. Reports its count through `onCount` for the tab badge.
 */
export default function TicketTestCasesTab({ ticketId, ticketCode, moduleName, onCount }) {
    const [linked, setLinked] = useState([]);
    const [library, setLibrary] = useState([]);
    // Search-and-link: typing filters the library; clicking (or Enter on) a
    // result links it straight away.
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [writing, setWriting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [viewing, setViewing] = useState(null); // a linked case opened in full
    const canEdit = hasPerm("Manage Test Cases");

    const show = useCallback((list) => {
        setLinked(list || []);
        onCount && onCount((list || []).length);
    }, [onCount]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [mine, all] = await Promise.all([
                qualityApi.getTicketTestCases(ticketId),
                qualityApi.getTestCases({ status: "Active" }),
            ]);
            show(mine.data);
            setLibrary(all.data || []);
        } catch (e) {
            showErrorToast(e.message || "Could not load test cases.");
        } finally {
            setLoading(false);
        }
    }, [ticketId, show]);

    useEffect(() => { load(); }, [load]);

    // Cases from the ticket's own module first, already-linked ones left out,
    // narrowed by every word typed (all words must match somewhere).
    const options = useMemo(() => {
        const have = new Set(linked.map((c) => c.code));
        const words = query.toLowerCase().split(/\s+/).filter(Boolean);
        return library
            .filter((c) => !have.has(c.code))
            .filter((c) => {
                if (!words.length) return true;
                const hay = [c.code, c.scenario, c.description, c.title, c.module, c.sub_module, c.screen, c.tags]
                    .join(" ").toLowerCase();
                return words.every((w) => hay.includes(w));
            })
            .sort((a, b) => (a.module === moduleName ? 0 : 1) - (b.module === moduleName ? 0 : 1) || a.code.localeCompare(b.code));
    }, [library, linked, moduleName, query]);
    const shown = options.slice(0, 50);

    const link = async (code) => {
        setBusy(true);
        try {
            const res = await qualityApi.linkTicketTestCase(ticketId, code);
            show(res.data);
            setQuery("");
            setActive(0);
            showSuccessToast(res.message || `${code} linked to ${ticketCode}.`);
        } catch (e) {
            showErrorToast(e.message || "Could not link the test case.");
        } finally {
            setBusy(false);
        }
    };

    const unlink = async (code) => {
        if (!window.confirm(`Unlink ${code} from ${ticketCode}? The case stays in the library.`)) return;
        setBusy(true);
        try {
            const res = await qualityApi.unlinkTicketTestCase(ticketId, code);
            show(res.data);
            showSuccessToast(`${code} unlinked from ${ticketCode}.`);
        } catch (e) {
            showErrorToast(e.message || "Could not unlink the test case.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="qa-page qa-page--embed">
            <MockBanner />
            <div className="qa-banner qa-banner--info">
                <span className="qa-banner__tx">
                    Link the test cases that prove this ticket works. They join the global library, and every later
                    regression run{moduleName ? <> for <b>{moduleName}</b></> : ""} includes them.
                </span>
            </div>

            <div className="qa-card qa-card--solo">
                <table className="qa-tbl">
                    <thead>
                        <tr>
                            <th>S.No</th><th>Test case</th><th>Test Case Scenario</th><th>Test Case Description</th>
                            <th>Test Type</th><th>Severity</th><th>Result</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={8} className="qa-empty">Loading…</td></tr>}
                        {!loading && linked.length === 0 && (
                            <tr>
                                <td colSpan={8} className="qa-empty">
                                    No test cases yet. This ticket can't be checked in regression until one is linked.
                                </td>
                            </tr>
                        )}
                        {linked.map((c, i) => (
                            <tr key={c.code} className="qa-click" title="Open the full test case" onClick={() => setViewing(c)}>
                                <td className="qa-num qa-muted">{i + 1}</td>
                                <td className="qa-code qa-mono">{c.code}</td>
                                <td className="qa-wrap">
                                    {c.scenario || <span className="qa-faint">—</span>}
                                    <div className="qa-hint">{c.module} · {c.sub_module}{c.screen ? ` · ${c.screen}` : ""}</div>
                                </td>
                                <td className="qa-wrap"><div className="qa-clamp">{c.description || c.title}</div></td>
                                <td><span className="qa-chip">{c.case_type}</span></td>
                                <td><SeverityChip value={c.severity} /></td>
                                <td className="qa-nw"><CaseResult testCase={c} /></td>
                                <td>
                                    {canEdit && (
                                        <button className="qa-btn qa-btn--sm qa-btn--danger" disabled={busy}
                                            onClick={(e) => { e.stopPropagation(); unlink(c.code); }}>
                                            Unlink
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {canEdit && (
                <div className="qa-form" style={{ marginTop: 14, alignItems: "end" }}>
                    <div className="qa-fl">
                        Link an existing test case
                        <div className="qa-search-pick">
                            <FiSearch size={13} className="qa-search-pick__i" />
                            <input
                                id={`qa-link-${ticketId}`} className="qa-input" value={query} autoComplete="off"
                                placeholder="Search code, scenario, description, module, screen…"
                                role="combobox" aria-expanded={open} aria-controls={`qa-link-list-${ticketId}`}
                                onFocus={() => setOpen(true)}
                                // Delay so a click on a result lands before the list closes.
                                onBlur={() => setTimeout(() => setOpen(false), 150)}
                                onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }}
                                onKeyDown={(e) => {
                                    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, shown.length - 1)); }
                                    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                                    else if (e.key === "Enter" && open && shown[active]) { e.preventDefault(); link(shown[active].code); }
                                    else if (e.key === "Escape") setOpen(false);
                                }}
                            />
                            {open && (
                                <div className="qa-search-pick__list" id={`qa-link-list-${ticketId}`} role="listbox">
                                    {shown.length === 0 && (
                                        <div className="qa-hint" style={{ padding: 8 }}>
                                            {library.length ? "No test case matches. Try other words, or write a new one." : "The library is empty. Write the first one."}
                                        </div>
                                    )}
                                    {shown.map((c, i) => (
                                        <button
                                            type="button" key={c.code} role="option" aria-selected={i === active} disabled={busy}
                                            className={i === active ? "qa-search-pick__on" : ""}
                                            onMouseEnter={() => setActive(i)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => link(c.code)}
                                        >
                                            <span className="qa-mono">{c.code}</span> {c.scenario || c.title}
                                            <span className="qa-hint"> · {c.module}{c.sub_module ? ` / ${c.sub_module}` : ""}{c.screen ? ` · ${c.screen}` : ""}</span>
                                            {c.description && c.description !== c.scenario && (
                                                <div className="qa-hint qa-trunc" style={{ maxWidth: "100%" }}>{c.description}</div>
                                            )}
                                        </button>
                                    ))}
                                    {options.length > shown.length && (
                                        <div className="qa-hint" style={{ padding: 8 }}>
                                            {options.length - shown.length} more. Type more words to narrow it down.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button className="qa-btn" onClick={() => setImporting(true)}
                            title={`Upload a sheet of test cases and link them all to ${ticketCode}`}>
                            <FiUploadCloud size={12} /> Import from Excel
                        </button>
                        <button className="qa-btn qa-btn--primary" onClick={() => setWriting(true)}>
                            <FiPlus size={12} /> Write a new one
                        </button>
                    </div>
                </div>
            )}

            {viewing && (
                <TestCaseForm
                    testCase={viewing}
                    context={{ ticketId, ticketCode }}
                    onClose={() => setViewing(null)}
                    onSaved={() => { setViewing(null); load(); }}
                    onDeleted={() => { setViewing(null); load(); }}
                    onUnlinked={() => load()}
                />
            )}

            {importing && (
                <TestCaseImportModal
                    ticketId={ticketId}
                    ticketCode={ticketCode}
                    onClose={() => setImporting(false)}
                    onImported={() => { setImporting(false); load(); }}
                />
            )}

            {writing && (
                <TestCaseForm
                    prefill={moduleName ? { module: moduleName } : undefined}
                    context={{ ticketId, ticketCode }}
                    onClose={() => setWriting(false)}
                    onSaved={() => { setWriting(false); load(); }}
                />
            )}
        </div>
    );
}
